import { ImgID } from "../../../../image_loader/img_id.js";
import { ColorScheme } from "../../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../../common_ui_elements/text_style_scheme.js";
import { UIElementHolder } from "../../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../../common_ui_elements/ui_text_element.js";
import { LocalizationManager } from "../../../../localization/localization_manager.js";
import { Vector2 } from "../../../../common/vector2.js";
import { Easing } from "../../../../transforms/transform_base.js";
import { TransformScale } from "../../../../transforms/transform_scale.js";

export class EscalatorHud extends UIElementHolder
{
  private static readonly BG_W = 433;
  private static readonly BG_H = 152;

  private static readonly LEVEL_X = 60;
  private static readonly LEVEL_Y = 8;
  private static readonly LEVEL_W = 313;
  private static readonly LEVEL_H = 62;
  private static readonly LEVEL_FONT = 46;

  private static readonly TIME_X = 140;
  private static readonly TIME_Y = 78;
  private static readonly TIME_W = 200;
  private static readonly TIME_H = 58;
  private static readonly TIME_FONT = 50;

  private static readonly CLOCK_X = 108.5;
  private static readonly CLOCK_Y = 102;
  private static readonly HAND_W = 26;
  private static readonly HAND_H = 12;
  private static readonly HAND_PIVOT_X = 5.5;
  private static readonly HAND_BASE_RAD = -Math.PI / 2;
  private static readonly HAND_STEP_RAD = Math.PI / 30;

  private static readonly FINAL_SECONDS = 5;
  private static readonly FINAL_POP_SCALE = 1.6;
  private static readonly FINAL_POP_MS = 350;
  private static readonly FINAL_POP_PEAK = 0.4;

  private readonly time_text: UITextElement;
  private readonly level_text: UITextElement;
  private readonly hand: UIElementHolder;
  private shown_seconds = -1;
  private shown_level = -1;

  constructor( ctx: CanvasRenderingContext2D, center_x: number, y: number )
  {
    const width = LayoutScaler.widthFromDesign( EscalatorHud.BG_W );
    const height = LayoutScaler.heightFromDesign( EscalatorHud.BG_H );
    const x = center_x - width / 2;

    super( ctx, x, y, width, height, false, false );
    this.setClickable( false );

    const bg = new UIImage( ctx, 0, 0, width, height, ImgID.UI_ConveyorHudBg );
    bg.setClickable( false );
    this.addChild( bg );

    this.level_text = this.createText( EscalatorHud.LEVEL_X, EscalatorHud.LEVEL_Y, EscalatorHud.LEVEL_W, EscalatorHud.LEVEL_H, EscalatorHud.LEVEL_FONT );
    this.time_text = this.createText( EscalatorHud.TIME_X, EscalatorHud.TIME_Y, EscalatorHud.TIME_W, EscalatorHud.TIME_H, EscalatorHud.TIME_FONT );
    this.hand = this.createHand();

    this.setRun( 0, 1 );
  }

  public setRun( seconds: number, level: number ): void
  {
    if ( seconds !== this.shown_seconds )
    {
      this.shown_seconds = seconds;
      this.time_text.setText( EscalatorHud.formatTime( seconds ) );
      this.hand.setRotationImmediate( EscalatorHud.HAND_BASE_RAD - seconds * EscalatorHud.HAND_STEP_RAD );
      this.updateFinalCountdown( seconds );
    }

    if ( level !== this.shown_level )
    {
      this.shown_level = level;
      this.level_text.setText( LocalizationManager.tWithParams( "CONVEYOR.WAVE", [ String( level ) ] ) );
    }
  }

  private updateFinalCountdown( seconds: number ): void
  {
    const is_final = seconds > 0 && seconds <= EscalatorHud.FINAL_SECONDS;

    this.time_text.stopAllTransforms();
    this.time_text.setScaleImmediate( new Vector2( 1, 1 ) );
    this.time_text.setTextColor( is_final ? ColorScheme.conveyor_hud_time_final : ColorScheme.white );

    if ( !is_final )
      return;

    const peak = new Vector2( EscalatorHud.FINAL_POP_SCALE, EscalatorHud.FINAL_POP_SCALE );

    this.time_text.addTransform( new TransformScale(
      this.time_text,
      false,
      new Vector2( 1, 1 ),
      peak,
      performance.now(),
      EscalatorHud.FINAL_POP_MS,
      {
        easing: Easing.EaseOutQuad,
        ping_pong_behaviour: true,
        ping_pong_threshold: EscalatorHud.FINAL_POP_PEAK,
      }
    ) );
  }

  private static formatTime( seconds: number ): string
  {
    const minutes = Math.floor( seconds / 60 );
    const rest = seconds % 60;

    return `${minutes}:${String( rest ).padStart( 2, "0" )}`;
  }

  private createText( dx: number, dy: number, dw: number, dh: number, font: number ): UITextElement
  {
    const x = LayoutScaler.widthFromDesign( dx );
    const y = LayoutScaler.heightFromDesign( dy );
    const w = LayoutScaler.widthFromDesign( dw );
    const h = LayoutScaler.heightFromDesign( dh );

    const text = new UITextElement( this.ctx, x, y, w, h, {
      text: "",
      font_size: LayoutScaler.textSizeFromDesign( font ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      using_y_offset: false,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_color: ColorScheme.conveyor_hud_text_outline,
      shadow_offset_x: 0,
      shadow_offset_y: 0,
    } );
    text.setClickable( false );
    this.addChild( text );

    return text;
  }

  private createHand(): UIElementHolder
  {
    const hand_w = LayoutScaler.widthFromDesign( EscalatorHud.HAND_W );
    const hand_h = LayoutScaler.heightFromDesign( EscalatorHud.HAND_H );
    const pivot_x = LayoutScaler.widthFromDesign( EscalatorHud.HAND_PIVOT_X );
    const holder_w = ( hand_w - pivot_x ) * 2;
    const holder_x = LayoutScaler.widthFromDesign( EscalatorHud.CLOCK_X ) - holder_w / 2;
    const holder_y = LayoutScaler.heightFromDesign( EscalatorHud.CLOCK_Y ) - hand_h / 2;
    const hand_x = holder_w / 2 - pivot_x;

    const holder = new UIElementHolder( this.ctx, holder_x, holder_y, holder_w, hand_h );
    holder.setClickable( false );
    holder.enableRotation();
    this.addChild( holder );

    const hand = new UIImage( this.ctx, hand_x, 0, hand_w, hand_h, ImgID.UI_ConveyorHudHand );
    hand.setClickable( false );
    holder.addChild( hand );

    return holder;
  }

  public name(): string
  {
    return "EscalatorHud";
  }
}
